/**
 * `NoMembershipBlock` — the hard-block full-screen takeover shown to a visitor
 * with NO membership (D-130, the proto's 'Ei jäsenyyttä' takeover from
 * `AppSidebar`). It covers the viewport: the person authenticated but is not a
 * member, so the only actions are joining (an external link) or logging out.
 *
 * Standalone terminal ROUTE (032 US2). Post-cutover this is the component
 * mounted at the public `/ei-jasenyytta` route, with `chrome: 'bare'` so it
 * renders with NO member shell behind it (D-130's "the member shell is NOT
 * rendered behind the hard-block" is satisfied by the bare route, NOT by a
 * Layout early-return as it was pre-cutover). The member callback 303s the
 * browser here (without minting a cookie) on a confirmed non-membership outcome,
 * so the visitor that reaches this route is ANONYMOUS. The ROUTE is the gate, so
 * the component renders UNCONDITIONALLY — no in-component `useMembershipStatus`
 * discriminator, no early `return null`. (042/D-202 binary access: an ended
 * (`lapsed`) membership is refused a session at sign-in and reaches THIS
 * terminal route too — there is no in-shell lapsed banner any more.)
 *
 * "Kirjaudu ulos" confirms application-session revocation before replacing the
 * page with `/login`. A failed revocation shows the shared retry screen. The
 * terminal route is public, so successful revocation needs explicit navigation.
 * Broker single-sign-out remains unconfirmed. The join destination is the
 * proto-authoritative `JOIN_URL` (D-107), shown inline in the body copy.
 *
 * Layering: a `fixed inset-0 z-50 bg-card` overlay — `z-50` is the app's top
 * layer (matching the `Dialog`/`Sheet` primitives) and `bg-card` is the
 * pure-white token standing in for the proto's `bg-white`. The colour comes from
 * a token-backed utility and the z-index from a scale step, never a raw hex or
 * an arbitrary `z-[…]` bracket (the architecture project enforces FR-013 /
 * FR-047). The inner `min-h-full` flex wrapper centres the panel when it fits and
 * scrolls cleanly (`overflow-y-auto`) when it does not.
 *
 * Accessibility: a `role="dialog"` + `aria-modal="true"` takeover named by its
 * `<h1>` (via `aria-labelledby`); focus moves to the heading on mount. It is a
 * HARD block — no Escape / overlay-click dismissal — and the bare route renders
 * no member shell behind it, so AT and tab cannot reach a background and focus
 * stays within the dialog without an explicit trap. The trailing external-link
 * icon is decorative (`aria-hidden`).
 */

import { type JSX, useEffect, useId, useRef } from 'react';

import { useNonMemberScreenCapture } from '../analytics/useNonMemberScreenCapture.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { Button } from '../primitives/Button.js';
import { useSignOut } from '../session/useSignOut.js';

import { ExternalLinkIcon } from './icons.js';

// Proto-authoritative join destination (D-107): the single non-member join URL.
const JOIN_URL = 'https://www.ytkpalvelut.fi/liity-jaseneksi/';

// The six member-benefit lines, in the proto's exact order. Listed as keys so
// the copy stays in the i18n bundle (fi authoritative, en parity).
const BENEFIT_KEYS = [
  'shell.noMembershipBlock.benefit1',
  'shell.noMembershipBlock.benefit2',
  'shell.noMembershipBlock.benefit3',
  'shell.noMembershipBlock.benefit4',
  'shell.noMembershipBlock.benefit5',
  'shell.noMembershipBlock.benefit6',
] as const;

const overlayClasses = 'fixed inset-0 z-50 overflow-y-auto bg-card';
const centerClasses = 'flex min-h-full items-center justify-center px-4 py-8';
const panelClasses = 'w-full max-w-md text-center';
const headingClasses = 'm-0 text-xl font-bold text-foreground outline-none';
const bodyClasses = 'mt-3 text-sm leading-relaxed text-muted-foreground';
const benefitsIntroClasses = 'mt-5 text-sm font-semibold text-foreground';
const benefitsListClasses = 'mt-2 list-disc space-y-1 pl-5 text-left text-sm text-muted-foreground';
const joinParaClasses = 'mt-5 text-sm leading-relaxed text-muted-foreground';
const joinLinkClasses =
  'inline-flex items-center gap-1 font-semibold text-primary underline underline-offset-2 ' +
  'hover:text-primary/90 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const joinLinkIconClasses = 'h-4 w-4';
const logoutRowClasses = 'mt-6 flex justify-center';

export function NoMembershipBlock(): JSX.Element {
  const t = useTranslate();
  const signOut = useSignOut();
  const { captureJoinClicked, captureLogoutClicked } = useNonMemberScreenCapture();
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Move focus INTO the takeover on mount so a keyboard / screen-reader user
  // lands on the dialog (its heading). The heading is programmatically focusable
  // (`tabIndex={-1}`); focusing it (not the logout action) announces the block's
  // purpose from the top. The component renders unconditionally (the bare route
  // is the gate), so the focus move runs on every mount.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div role="dialog" aria-modal="true" aria-labelledby={headingId} className={overlayClasses}>
      <div className={centerClasses}>
        <div className={panelClasses}>
          <h1 ref={headingRef} tabIndex={-1} id={headingId} className={headingClasses}>
            {t('shell.noMembershipBlock.heading')}
          </h1>
          <p className={bodyClasses}>{t('shell.noMembershipBlock.body')}</p>
          <p className={benefitsIntroClasses}>{t('shell.noMembershipBlock.benefitsIntro')}</p>
          <ul className={benefitsListClasses}>
            {BENEFIT_KEYS.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>
          <p className={joinParaClasses}>
            {t('shell.noMembershipBlock.joinPrefix')}
            <a
              href={JOIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={joinLinkClasses}
              onClick={captureJoinClicked}
            >
              {t('shell.noMembershipBlock.joinLinkLabel')}
              <ExternalLinkIcon aria-hidden="true" className={joinLinkIconClasses} />
            </a>
          </p>
          <div className={logoutRowClasses}>
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={() => {
                captureLogoutClicked();
                void signOut();
              }}
            >
              {t('shell.noMembershipBlock.logout')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
